<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      // Esta é a classe que controla o estado retraído/expandido
      isVerticalMenuCollapsed ? 'menu-collapsed' : 'menu-expanded',
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
  >

    <div
      class="navbar-header"
      :class="{ 'expanded': !isVerticalMenuCollapsed }"
    >
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              :to="isExternalUser() ? { name: 'portaldocumentos-externo' } : { name: 'home' }"
              :disabled="isNotAccessAuthenticated()"
            >
              <span class="brand-logo">
                <b-img
                  src="@/assets/images/logo/logoverde.png"
                  alt="logo"
                />
              </span>
              <h2 class="brand-text">
                {{ appName }}
              </h2>
            </b-link>
          </li>

          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <div
      class="shadow-bottom"
      :class="{ 'd-block': shallShadowBottom }"
    />

    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="onScrollY"
    >

      <li
        class="nav-item nav-top-toggle"
        @click="toggleCollapsed"
      >
        <b-link class="d-flex align-items-center">
          <feather-icon
            icon="MenuIcon"
            size="20"
            class="collapse-toggle-icon"
          />
          <span class="menu-title text-truncate">Recolher</span>
        </b-link>
      </li>
      <vertical-nav-menu-items
        :key="componentKey"
        :items="navMenuItems.menus"
        class="navigation navigation-main"
      />

    </vue-perfect-scrollbar>
  </div>
</template>

<script>
import {
  collapseTogglerIcon,
} from '@fortawesome/free-solid-svg-icons'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import {
  provide,
  ref,
  onMounted,
  onUnmounted,
  reactive,
} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import { isExternalAccess, isAccessNotAuthenticated } from '@/auth/utils'
import getNavMenuItems from '@/navigation/vertical-interno'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      collapseTogglerIcon,
    }
  },
  setup(props) {
    const { skin } = useAppConfig()

    const navMenuItems = reactive({ menus: [] })

    const {
      isVerticalMenuCollapsed,
      toggleCollapsed,
    } = useVerticalNavMenu(props)

    const shallShadowBottom = ref(false)
    const componentKey = ref(0)

    async function loadMenu() {
      navMenuItems.menus = await getNavMenuItems()
      componentKey.value += 1
    }

    function onUpdateMenu() {
      loadMenu()
    }

    onMounted(async () => {
      await loadMenu()
      window.addEventListener('update-menu', onUpdateMenu)
    })

    onUnmounted(() => {
      window.removeEventListener('update-menu', onUpdateMenu)
    })

    provide('isVerticalMenuCollapsed', isVerticalMenuCollapsed)

    const toggleMenuCollapsed = desired => {
      const want = typeof desired === 'boolean' ? desired : !isVerticalMenuCollapsed.value
      if (isVerticalMenuCollapsed.value !== want) toggleCollapsed()
    }

    provide('toggleMenuCollapsed', toggleMenuCollapsed)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const { appName, appLogoImage } = $themeConfig.app

    const isExternalUser = () => isExternalAccess()
    const isNotAccessAuthenticated = () => isAccessNotAuthenticated()

    const getHomePage = () => {
      if (localStorage.moduloSelecionado === 'Comercial') return 'dashboards-comercial'
      if (
        localStorage.moduloSelecionado === 'Cobranca'
        || localStorage.moduloSelecionado === 'Cobrança'
      ) return 'dashboards-cobrancas'
      if (localStorage.moduloSelecionado === 'Plataforma') return 'home-plataforma'
      return 'home'
    }

    const onScrollY = evt => {
      shallShadowBottom.value = evt.srcElement.scrollTop > 0
    }

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      toggleCollapsed,
      shallShadowBottom,
      skin,
      appName,
      appLogoImage,
      getHomePage,
      isExternalUser,
      isNotAccessAuthenticated,
      componentKey,
      onScrollY,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

.main-menu .navbar-header .brand-text {
  font-family: 'Poppins', sans-serif !important;
  font-weight: 700 !important;
  /* 700 é o valor para "bold" */
}

/* --- CSS ADICIONADO PARA HOVER EM NEGRITO --- */
/* 1. Remove as animações de "deslizar" e "glow" do HOVER */
.main-menu .navigation-main li:not(.active):not(.nav-top-toggle)>a:hover,
.nav-top-toggle .d-flex:hover {
  transform: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* 2. Adiciona o negrito ao TÍTULO no hover (apenas menu expandido) */
.main-menu.menu-expanded .navigation-main li:not(.nav-top-toggle) a:hover .menu-title {
  font-weight: 700;
  /* semibold */
}

/* 3. Adiciona um efeito "bold" ao ÍCONE no hover (ambos os estados) */
.main-menu .navigation-main li:not(.nav-top-toggle) a:hover svg,
.nav-top-toggle .d-flex:hover svg {
  stroke-width: 2.5px;
}

/* --- FIM DO CSS DE HOVER --- */

/* * --- CSS PARA CENTRALIZAR O BOTÃO --- */
.nav-top-toggle {
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  cursor: pointer;

  .d-flex {
    font-size: 1rem;
    padding-top: .55rem;
    padding-bottom: .55rem;
    color: inherit;
    justify-content: center !important;
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
  }

  .collapse-toggle-icon {
    width: 16px;
    height: 16px;
    font-size: 1.1rem;
    margin-right: 0 !important;
  }

  .menu-title {
    display: none !important;
  }
}

/* --- FIM DO CSS ADICIONADO --- */

/* ===== Itens PRINCIPAIS (nível 1) ===== */
.main-menu {

  /* Aplicado a todos os LIs, exceto o nosso botão */
  .navigation-main>li:not(.nav-top-toggle)>a {
    font-size: 1rem;
    padding-top: .55rem;
    padding-bottom: .55rem;
  }

  .navigation-main>li:not(.nav-top-toggle)>a .menu-title {
    font-size: inherit;
    line-height: 1.25rem;
  }

  /* Ícones do nível 1 */
  .navigation-main>li:not(.nav-top-toggle)>a svg,
  .navigation-main>li:not(.nav-top-toggle)>a i {
    width: 16px;
    height: 16px;
    font-size: 1.1rem;
    margin-right: .9rem;
  }

  /* ===== Submenus (nível 2) ===== */
  .navigation-main .menu-content>li>a {
    font-size: 0.5rem;
    line-height: 1.2;
    display: flex;
    align-items: center;
    padding-left: 1.7rem;
    padding-right: 1rem;
    padding-top: .45rem;
    padding-bottom: .45rem;
  }

  .navigation-main .menu-content>li>a .menu-title {
    font-size: inherit;
    line-height: 1.2;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    word-break: break-word;
  }

  /* Ícones dos submenus */
  .navigation-main .menu-content>li>a svg,
  .navigation-main .menu-content>li>a i {
    width: 14px;
    height: 14px;
    font-size: 1rem;
    margin-right: .65rem;
  }

  /* ===== Submenu de submenu (nível 3+) – opcional ===== */
  .navigation-main .menu-content .menu-content>li>a {
    font-size: 0.78rem;
    padding-left: 2.1rem;
  }
}

/* ===== Comportamento quando COLAPSADO ===== */
.main-menu:not(.menu-expanded) {
  .navigation-main li.has-sub>a::after {
    display: none;
  }

  /* Aplicado a todos os LIs, exceto o nosso botão */
  .navigation-main>li:not(.nav-top-toggle)>a {
    justify-content: center;
    padding-left: 1.5rem;
    padding-right: 0.75rem;
  }

  .navigation-main>li>a .menu-title {
    display: none;
  }

  .navigation-main .menu-content {
    display: none;
  }
}

/* ===== Comportamento quando EXPANDIDO ===== */
.main-menu.menu-expanded {

  /* Aplicado a todos os LIs, exceto o nosso botão */
  .navigation-main>li:not(.nav-top-toggle)>a {
    justify-content: flex-start;
    padding-left: 1.25rem;
    padding-right: 1rem;
  }

  .navigation-main>li>a .menu-title {
    display: inline-flex;
  }

  .navigation-main .menu-content {
    display: block;
  }

  .navigation-main .menu-content>li>a .menu-title {
    display: block;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    word-break: break-word;
    line-height: 1.2;
  }
}
</style>
